import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "js/components/ui/button";
import { cn } from "lib/utils";

export interface PaginationState {
  current: number;
  pageSize: number;
  total: number;
}

interface TablePaginationProps {
  pagination: PaginationState;
  onChange: (page: number) => void;
  className?: string;
  isLoading?: boolean;
}

const getPageNumbers = (current: number, totalPages: number, siblingCount = 1) => {
  const totalNumbers = siblingCount * 2 + 3; // current + siblings + first & last
  const totalBlocks = totalNumbers + 2; // including ellipsis

  if (totalPages <= totalBlocks) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(current - siblingCount, 1);
  const rightSiblingIndex = Math.min(current + siblingCount, totalPages);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

  const firstPage = 1;
  const lastPage = totalPages;

  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const leftItemCount = siblingCount * 2 + 3;
    return [
      ...Array.from({ length: leftItemCount }, (_, i) => i + 1),
      "ellipsis",
      lastPage,
    ];
  }

  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    const rightItemCount = siblingCount * 2 + 3;
    return [
      firstPage,
      "ellipsis",
      ...Array.from({ length: rightItemCount }, (_, i) => totalPages - rightItemCount + i + 1),
    ];
  }

  return [
    firstPage,
    "ellipsis",
    ...Array.from({ length: rightSiblingIndex - leftSiblingIndex + 1 }, (_, i) =>
      leftSiblingIndex + i,
    ),
    "ellipsis",
    lastPage,
  ];
};

const TablePagination: React.FC<TablePaginationProps> = ({
  pagination,
  onChange,
  className,
  isLoading,
}) => {
  const { current, pageSize, total } = pagination;
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const safeCurrent = Math.min(current, totalPages);

  const start = total === 0 ? 0 : (safeCurrent - 1) * pageSize + 1;
  const end = Math.min(safeCurrent * pageSize, total);

  const handleChange = (page: number) => {
    if (page < 1 || page > totalPages || page === safeCurrent || isLoading) return;
    onChange(page);
  };

  const pages = getPageNumbers(safeCurrent, totalPages);

  return (
    <div className={cn("flex flex-col gap-2 text-sm text-slate-500 md:flex-row md:items-center md:justify-between", className)}>
      <span>
        Mostrando {start}-{end} de {total.toLocaleString("es-MX")}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => handleChange(safeCurrent - 1)}
          disabled={safeCurrent === 1 || isLoading}
        >
          <ChevronLeft className="size-4" />
          <span className="sr-only">Anterior</span>
        </Button>
        {pages.map((page, index) =>
          page === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className="px-2 text-slate-400">
              …
            </span>
          ) : (
            <Button
              key={page}
              variant={page === safeCurrent ? "default" : "ghost"}
              size="sm"
              className={cn(
                "h-8 w-8 p-0",
                page === safeCurrent
                  ? "bg-[#0060ff] text-white hover:bg-[#0052d4]"
                  : "text-slate-600 hover:bg-slate-100",
              )}
              onClick={() => handleChange(page as number)}
              disabled={isLoading}
            >
              {page}
            </Button>
          ),
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => handleChange(safeCurrent + 1)}
          disabled={safeCurrent === totalPages || isLoading}
        >
          <ChevronRight className="size-4" />
          <span className="sr-only">Siguiente</span>
        </Button>
      </div>
    </div>
  );
};

export { TablePagination };
